import React from 'react';
import { Route, Routes } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { CircularProgress, Skeleton } from '@mui/material';
import Typography from '@mui/material/Typography';
import { TranslationKeys } from '../../../../translations';
import { ConfirmDialog } from '../../../../components';
import TabOutlet from '../../../../components/TabOutlet';
import { NotFoundView } from '../../../../views';
import { OwnerEmployers } from './OwnerEmployers';
import { OwnerListDialog } from './OwnerListDialog';
import { ActionButtonStyled, DivHeaderActionsStyled } from './OwnerDetailPage.styles';
import { useOwnerDetailPage } from './useOwnerDetailPage';
import { ProgressContainerStyled } from './OwnerInformationTab/OwnerInformationTab.styles';

export const OwnerDetailPage = () => {
  const {
    owner,
    isOwner,
    isSuperAdmin,
    ownerEmployers,
    isLoadingOwnerEmployers,
    employersPage,
    employersPageSize,
    employersPageSizeOptions,
    employersTotalCount,
    onEmployersPaginationChange,
    existsOwner,
    showDeleteConfirmationDialog,
    closeDeleteConfirmationDialog,
    openDeleteConfirmationDialog,
    showViewEmployersDialog,
    closeViewEmployersDialog,
    openViewEmployersDialog,
    disabledShowEmployersButtonIfLoadingOrNoAccess,
    disabledDeleteButtonIfLoadingOrNoAccess,
    onClickConfirmDelete,
  } = useOwnerDetailPage();

  const tabData = React.useMemo(() => {
    const tabs = [
      {
        path: 'info',
        label: <FormattedMessage id={TranslationKeys.owners_tab_general} />,
      },
    ];

    if (isSuperAdmin) {
      tabs.push({
        path: 'insurer-accesses',
        label: <FormattedMessage id={TranslationKeys.owners_tab_insurerAccesses} />,
      });
    }

    return tabs;
  }, [isSuperAdmin]);

  const ownerEmployersTitle = React.useMemo(() => {
    if (owner) {
      return (
        <span>
          {`${owner.ownerName}: ${employersTotalCount ?? ''} `}
          <FormattedMessage id={TranslationKeys.owners_employers} />
        </span>
      );
    }
    return <Skeleton width={100} />;
  }, [owner, employersTotalCount]);

  const breadcrumbTitle = React.useMemo(() => {
    if (owner?.ownerName) {
      return <Typography variant="h5">{owner.ownerName}</Typography>;
    }
    return (
      <Typography variant="h5">
        <Skeleton variant={'text'} width={200} />
      </Typography>
    );
  }, [owner]);

  const breadcrumbButtons = React.useMemo(() => {
    return (
      <DivHeaderActionsStyled>
        {isOwner && (
          <ActionButtonStyled
            messageId={TranslationKeys.owners_showEmployers}
            onClick={openViewEmployersDialog}
            disabled={disabledShowEmployersButtonIfLoadingOrNoAccess || !owner}
          />
        )}
        {isSuperAdmin ? (
          <ActionButtonStyled
            messageId={TranslationKeys.global_delete}
            onClick={openDeleteConfirmationDialog}
            disabled={disabledDeleteButtonIfLoadingOrNoAccess}
            variant="outlined"
          />
        ) : (
          false
        )}
      </DivHeaderActionsStyled>
    );
  }, [
    owner,
    isOwner,
    openViewEmployersDialog,
    disabledShowEmployersButtonIfLoadingOrNoAccess,
    openDeleteConfirmationDialog,
    disabledDeleteButtonIfLoadingOrNoAccess,
    isSuperAdmin,
  ]);

  const breadcrumb = React.useMemo(() => {
    return {
      title: owner?.ownerName,
      data: {
        titleElement: breadcrumbTitle,
        otherElements: breadcrumbButtons,
      },
    };
  }, [owner, breadcrumbTitle, breadcrumbButtons]);

  return (
    <>
      {existsOwner && (
        <TabOutlet tabs={tabData} breadcrumb={breadcrumb}>
          {showDeleteConfirmationDialog && (
            <ConfirmDialog
              title={<FormattedMessage id={TranslationKeys.global_delete} />}
              open={showDeleteConfirmationDialog}
              onClose={closeDeleteConfirmationDialog}
              onConfirm={onClickConfirmDelete}
            >
              <FormattedMessage id={TranslationKeys.global_deleteConfirmationMessage} />
            </ConfirmDialog>
          )}

          {showViewEmployersDialog && (
            <OwnerListDialog
              open={showViewEmployersDialog}
              title={ownerEmployersTitle}
              onClose={closeViewEmployersDialog}
            >
              {isLoadingOwnerEmployers ? (
                <ProgressContainerStyled>
                  <CircularProgress />
                </ProgressContainerStyled>
              ) : (
                <OwnerEmployers
                  ownerEmployers={ownerEmployers ?? []}
                  page={employersPage}
                  pageSize={employersPageSize}
                  totalCount={employersTotalCount}
                  pageSizeOptions={employersPageSizeOptions}
                  onPaginationChange={onEmployersPaginationChange}
                />
              )}
            </OwnerListDialog>
          )}
        </TabOutlet>
      )}

      {!existsOwner && (
        <Routes>
          <Route element={<NotFoundView />} path="*" />
        </Routes>
      )}
    </>
  );
};
